import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { useWorkosAuthEnabled } from "@/lib/workos-auth";
import { BlackGitHubIcon, WhiteGitHubIcon } from "./GitHubIcon";
import { Button } from "@repo/blacksmith-ui/button";
import FadeIn from "./FadeIn";
import { AnimatedBlacksmithLogoOneline, BlacksmithLogomarkAnimationSVG } from "./BlacksmithLogoSVG";
import { usePostHog } from "posthog-js/react";
import { CORE_ANALYTICS_EVENTS } from "@/lib/posthog-constants";
import { useTheme } from "next-themes";
import { useDashboardSearchParams as useSearchParams } from "@/hooks/useDashboardNavigation";
import { withFirstTouchRdtCid } from "@/lib/appFirstTouchAttribution";
import { isCodesmithExtensionEmbed } from "@/lib/codesmithExtensionEmbed";
import { WorkOSLoginPage } from "./workos/WorkOSLoginPage";
export function LoginCard() {
  const $ = _c(16);
  const {
    workosAuthEnabled,
    isLoading: isFlagLoading
  } = useWorkosAuthEnabled();
  const [isLoading, setIsLoading] = useState(false);
  const posthog = usePostHog();
  const {
    resolvedTheme
  } = useTheme();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== posthog || $[1] !== searchParams || $[2] !== workosAuthEnabled) {
    t0 = () => {
      setIsLoading(true);
      posthog?.capture(CORE_ANALYTICS_EVENTS.LOGIN_BUTTON_CLICK, {
        authentication_provider: workosAuthEnabled ? "workos" : "github"
      });
      const redirectUrl = encodeURIComponent(withFirstTouchRdtCid(window.location.href));
      const loginUrl = `${process.env.NEXT_PUBLIC_API_URL}/login/${workosAuthEnabled ? "workos" : "github"}?redirect=${redirectUrl}`;
      if (isCodesmithExtensionEmbed(searchParams.get("embed") ?? undefined)) {
        window.open(loginUrl, "_blank");
        setIsLoading(false);
        return;
      }
      window.location.href = loginUrl;
    };
    $[0] = posthog;
    $[1] = searchParams;
    $[2] = workosAuthEnabled;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const handleLogin = t0;
  if (isLoading) {
    let t1;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex h-screen items-center justify-center"><div className="w-32 h-32"><BlacksmithLogomarkAnimationSVG /></div></div>;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="w-48 mx-auto py-4 mb-0 text-neutral-900 dark:text-blacksmith-yellow"><AnimatedBlacksmithLogoOneline /></div>;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="-mt-4 flex flex-col text-neutral-800 dark:text-white"><span className="text-xl font-medium">Sign in</span><span className="text-sm text-neutral-500 dark:text-neutral-400">to continue to Blacksmith</span></div>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== resolvedTheme) {
    t3 = <>{resolvedTheme === "dark" ? <BlackGitHubIcon /> : <WhiteGitHubIcon />}Continue with GitHub</>;
    $[7] = resolvedTheme;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== handleLogin || $[10] !== isFlagLoading || $[11] !== t3) {
    t4 = <Button disabled={isFlagLoading} onClick={handleLogin} className="flex gap-2" variant="default" data-test="github-login-button">{t3}</Button>;
    $[9] = handleLogin;
    $[10] = isFlagLoading;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="-mt-2 block text-sm text-neutral-500 dark:text-neutral-400">No account? We'll create one for you.</span>;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4) {
    t6 = <div className="w-[360px] flex flex-col gap-6 text-center rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-8">{t1}{t2}{t4}{t5}</div>;
    $[14] = t4;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
export default function SignedOut(t0) {
  const $ = _c(14);
  const {
    autoLogin: t1
  } = t0;
  const autoLogin = t1 === undefined ? false : t1;
  const searchParams = useSearchParams();
  let t2;
  if ($[0] !== searchParams) {
    t2 = searchParams.get("autoLogin");
    $[0] = searchParams;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const autoLoginParam = t2;
  const {
    workosAuthEnabled,
    isLoading: isFlagLoading
  } = useWorkosAuthEnabled();
  let t3;
  if ($[2] !== autoLoginParam || $[3] !== searchParams) {
    t3 = autoLoginParam === "false" || isCodesmithExtensionEmbed(searchParams.get("embed") ?? undefined);
    $[2] = autoLoginParam;
    $[3] = searchParams;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const autoLoginDisabled = t3;
  let t4;
  if ($[5] !== autoLogin || $[6] !== searchParams) {
    t4 = autoLogin || searchParams.has("autoLogin");
    $[5] = autoLogin;
    $[6] = searchParams;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const autoLoginRequested = t4;
  const shouldRedirectToLogin = !isFlagLoading && !autoLoginDisabled && autoLoginRequested;
  let t5;
  let t6;
  if ($[8] !== shouldRedirectToLogin) {
    t5 = () => {
      if (!shouldRedirectToLogin) {
        return;
      }
      const redirectUrl = encodeURIComponent(withFirstTouchRdtCid(window.location.href));
      window.location.replace(`${process.env.NEXT_PUBLIC_API_URL}/login/github?redirect=${redirectUrl}`);
    };
    t6 = [shouldRedirectToLogin];
    $[8] = shouldRedirectToLogin;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  if (shouldRedirectToLogin || isFlagLoading) {
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="flex h-screen items-center justify-center bg-app-background"><div className="w-32 h-32"><BlacksmithLogomarkAnimationSVG /></div></div>;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  if (workosAuthEnabled) {
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <WorkOSLoginPage />;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    return t7;
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex flex-col items-center justify-center min-h-screen bg-app-background"><FadeIn><LoginCard /></FadeIn></div>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}